import React from 'react';
import { Link } from 'react-router-dom';
import * as ROUTES from '../../constants/routes';
import './franchiseProduct.css';

const navItems = [
  { href: '#overview', label: 'Overview' },
  { href: '#branding', label: 'Branding' },
  { href: '#studio', label: 'Studio' },
  { href: '#operations', label: 'Operations' },
  { href: '#crm', label: 'CRM' },
  { href: '#media', label: 'Media' },
];

const heroStats = [
  { label: 'Core Product Domains', value: '5' },
  { label: 'Section Routes', value: '15+' },
  { label: 'High-Value Workflows', value: '30+' },
];

const capabilityChips = [
  'Branding & Marketing',
  'Studio Design',
  'Academy + Events',
  'Templates + Support',
  'CRM + Automations',
];

const productPillars = [
  {
    id: 'overview',
    eyebrow: 'Franchise Shell',
    title: 'One Unified Franchise Workspace',
    summary:
      'The franchise dashboard centralizes identity, status, navigation, alerts, and execution so partners work from one reliable command surface.',
    bullets: [
      'Role-aware access with section-level route sync',
      'Franchisee profile + card + signed agreement visibility',
      'Status-first home experience and modal card export',
      'Persistent navigation state and contextual notification layer',
    ],
    mediaLabel: 'Dashboard Story Video Placeholder',
    mediaHint: 'Use this area for an end-to-end shell walkthrough',
  },
  {
    id: 'branding',
    eyebrow: 'Branding & Marketing',
    title: 'Brand Kit, Printables, and Asset Distribution',
    summary:
      'Brand governance is productized through guided generators and reusable brand surfaces so every output stays consistent and market-ready.',
    bullets: [
      'Brand Kit embedding with standalone open options',
      'Business cards, letterheads, signatures, and profile visuals',
      'Advanced printables pipeline with coming-soon modules already scaffolded',
      'Asset library support for storing and reusing generated collateral',
    ],
    mediaLabel: 'Branding Gallery Placeholder',
    mediaHint: 'Add branded screenshots, PDF samples, and before/after creative',
  },
  {
    id: 'studio',
    eyebrow: 'Studio Engine',
    title: 'Design Creation With Real Production Controls',
    summary:
      'Studio brings project-based creative operations into the platform with templates, custom sizes, layers, uploads, and editing workflows.',
    bullets: [
      'Preset-driven launch across social and print surfaces',
      'Custom canvas builder with naming and validation',
      'Layer stack, transform controls, alignment guides, and autosave',
      'Dedicated editor routes for in-dashboard and standalone editing',
    ],
    mediaLabel: 'Studio Product Demo Placeholder',
    mediaHint: 'Reserve for UI walkthrough video + editor close-up stills',
  },
  {
    id: 'operations',
    eyebrow: 'Ops Modules',
    title: 'Training, Events, Templates, Support, and Mandates',
    summary:
      'Beyond sales, the platform handles enablement and daily execution through academy sessions, events, templates, support workflows, and exclusive mandates.',
    bullets: [
      'Academy schedules with session links and calendar actions',
      'Templates list, editor, generate wizard, and documents archive',
      'Ticketed support flow with article search and follow-up threads',
      'Exclusive mandate exploration with project details and registration',
    ],
    mediaLabel: 'Operations Gallery Placeholder',
    mediaHint: 'Use this space for academy/event/templates/support screens',
  },
  {
    id: 'crm',
    eyebrow: 'CRM Layer',
    title: 'Full Funnel Revenue Operations',
    summary:
      'The owner CRM includes lead capture, lifecycle execution, deal progression, activities, automations, and team management for conversion at scale.',
    bullets: [
      'KPI and funnel dashboard with source performance and quick actions',
      'Leads workspace with filters, saved views, import/export, and bulk controls',
      'Deal, contact, activity, property, duplicate, audit, and source modules',
      'Automations + team seat lifecycle for controlled scale and governance',
    ],
    mediaLabel: 'CRM Pipeline Showcase Placeholder',
    mediaHint: 'Insert sales pipeline visuals, board view shots, and lifecycle demos',
  },
];

const mediaLibrarySlots = [
  { title: 'Hero Franchise Product Film', type: 'Video Slot', ratio: '16:9' },
  { title: 'Dashboard Hero Capture', type: 'Image Slot', ratio: '16:9' },
  { title: 'Branding Suite Walkthrough', type: 'Video Slot', ratio: '16:9' },
  { title: 'Studio Editor Deep-Dive', type: 'Video Slot', ratio: '16:9' },
  { title: 'Templates + Docs Showcase', type: 'Image Slot', ratio: '4:3' },
  { title: 'Support + Ticket Flow', type: 'Image Slot', ratio: '4:3' },
  { title: 'Mandates + Event Screens', type: 'Image Slot', ratio: '4:3' },
  { title: 'CRM Funnel + Automations', type: 'Video Slot', ratio: '16:9' },
];

const PillarSection = ({ pillar, reverse }) => (
  <section id={pillar.id} className={`fp-pillar ${reverse ? 'reverse' : ''}`}>
    <article className="fp-pillar-copy">
      <div className="fp-pillar-eyebrow">{pillar.eyebrow}</div>
      <h2>{pillar.title}</h2>
      <p>{pillar.summary}</p>
      <ul>
        {pillar.bullets.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </article>
    <article className="fp-pillar-media">
      <div className="fp-media-frame large">
        <span>{pillar.mediaLabel}</span>
      </div>
      <p>{pillar.mediaHint}</p>
      <div className="fp-media-row">
        <div className="fp-media-frame small"><span>Image Placeholder</span></div>
        <div className="fp-media-frame small"><span>Video Placeholder</span></div>
      </div>
    </article>
  </section>
);

const FranchiseProductPage = () => (
  <div className="franchise-product-page">
    <div className="fp-bg-glow fp-bg-glow-left" />
    <div className="fp-bg-glow fp-bg-glow-right" />

    <nav className="fp-nav">
      <Link to={ROUTES.LANDING} className="fp-nav-brand" aria-label="Douglas & McCartney">
        <img
          src="/Douglas2-removebg-preview.png"
          alt="Douglas & McCartney"
          className="fp-nav-brand-logo"
          loading="eager"
          decoding="async"
        />
      </Link>
      <div className="fp-nav-links">
        {navItems.map((item) => (
          <a key={item.href} href={item.href}>
            {item.label}
          </a>
        ))}
      </div>
      <div className="fp-nav-cta">
        <Link to={ROUTES.SIGN_IN}>Log In</Link>
        <Link to={ROUTES.FRANCHISE_REGISTRATION_DASHBOARD} className="primary">Sign Up</Link>
      </div>
    </nav>

    <header className="fp-hero" id="top">
      <article className="fp-hero-copy">
        <div className="fp-hero-overline">Product Experience</div>
        <h1>Franchise Dashboard, Rebuilt as a Sales-Ready Story</h1>
        <p>
          This page is designed for potential leads to quickly understand what they get after joining:
          from brand production and training to CRM execution and operational scale.
        </p>
        <div className="fp-chip-row">
          {capabilityChips.map((chip) => (
            <span key={chip} className="fp-chip">{chip}</span>
          ))}
        </div>
        <div className="fp-hero-actions">
          <Link to={ROUTES.FRANCHISE_REGISTRATION_DASHBOARD} className="fp-btn primary">
            Become a Franchisee
          </Link>
          <a className="fp-btn" href="#media">
            Watch Product Story
          </a>
        </div>
      </article>
      <article className="fp-hero-media">
        <div className="fp-hero-media-stack">
          <div className="fp-media-frame hero">
            <span>Hero Product Video Placeholder</span>
          </div>
          <div className="fp-media-frame hero-mini">
            <span>Live Product Demo Placeholder</span>
          </div>
        </div>
      </article>
    </header>

    <section className="fp-stats">
      {heroStats.map((stat) => (
        <article key={stat.label} className="fp-stat-card">
          <h3>{stat.value}</h3>
          <p>{stat.label}</p>
        </article>
      ))}
    </section>

    <section className="fp-intro-strip">
      <h2>Built for lead confidence, conversion, and delivery</h2>
      <p>
        Every module is presented as a practical capability, not just a feature list. Add real media into
        the placeholders to turn this into a high-performing product narrative.
      </p>
    </section>

    <div className="fp-pillars">
      {productPillars.map((pillar, index) => (
        <PillarSection key={pillar.id} pillar={pillar} reverse={index % 2 === 1} />
      ))}
    </div>

    <section id="media" className="fp-media-library">
      <header className="fp-media-library-head">
        <h2>Media Library Placeholders</h2>
        <p>
          Drop in final visuals from your dashboard, campaign creatives, and walkthrough captures.
          This block is intentionally spacious for high-impact media-first storytelling.
        </p>
      </header>
      <div className="fp-media-library-grid">
        {mediaLibrarySlots.map((slot) => (
          <article key={slot.title} className="fp-media-library-card">
            <div className="fp-media-frame library">
              <span>{slot.type}</span>
            </div>
            <h4>{slot.title}</h4>
            <p>Ratio: {slot.ratio}</p>
          </article>
        ))}
      </div>
    </section>

    <section id="cta" className="fp-final-cta">
      <h2>Ready to launch your franchise dashboard journey?</h2>
      <p>
        Use this page as your product-led conversion funnel. Keep the visuals current, and route leads
        directly into registration with one click.
      </p>
      <div className="fp-final-actions">
        <Link to={ROUTES.FRANCHISE_REGISTRATION_DASHBOARD} className="fp-btn primary">
          Start Registration
        </Link>
        <Link to="/contact" className="fp-btn">
          Talk to Team
        </Link>
      </div>
    </section>
  </div>
);

export default FranchiseProductPage;
